import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrganization, OrganizationMember, OrganizationInvite, OrganizationRole } from '../context/OrganizationContext';
import TopBar from '../components/TopBar';

type Tab = 'general' | 'members' | 'billing';

const OrganizationSettings: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const {
    currentOrganization,
    organizations,
    updateOrganization,
    deleteOrganization,
    getMembers,
    updateMemberRole,
    removeMember,
    getPendingInvites,
    createInvite,
    cancelInvite,
    isOwner
  } = useOrganization();

  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Members state
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invites, setInvites] = useState<OrganizationInvite[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrganizationRole>('member');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  // Find the organization
  const organization = organizations.find(o => o.id === orgId) || currentOrganization;

  // Initialize name from organization
  useEffect(() => {
    if (organization) {
      setName(organization.name);
    }
  }, [organization]);

  // Load members and invites
  useEffect(() => {
    if (orgId && activeTab === 'members') {
      loadMembers();
    }
  }, [orgId, activeTab]);

  const loadMembers = async () => {
    if (!orgId) return;

    setIsLoadingMembers(true);
    try {
      const [membersList, invitesList] = await Promise.all([
        getMembers(orgId),
        isOwner ? getPendingInvites(orgId) : Promise.resolve([])
      ]);
      setMembers(membersList);
      setInvites(invitesList);
    } catch (error) {
      console.error('Failed to load members:', error);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleSave = async () => {
    if (!orgId || !name.trim()) return;

    setIsSaving(true);
    setSaveMessage(null);

    try {
      await updateOrganization(orgId, { name: name.trim() });
      setSaveMessage({ type: 'success', text: 'Organization updated successfully' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error: any) {
      setSaveMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update organization' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!orgId || deleteConfirmName !== organization?.name) return;

    try {
      await deleteOrganization(orgId);
      navigate('/');
    } catch (error) {
      console.error('Failed to delete organization:', error);
    }
  };

  const handleInvite = async () => {
    if (!orgId || !inviteEmail.trim()) return;

    setIsInviting(true);
    setInviteError(null);

    try {
      await createInvite(orgId, inviteEmail.trim(), inviteRole);
      setInviteEmail('');
      setInviteRole('member');
      await loadMembers();
    } catch (error: any) {
      setInviteError(error.response?.data?.error || 'Failed to send invite');
    } finally {
      setIsInviting(false);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    if (!orgId) return;

    try {
      await cancelInvite(orgId, inviteId);
      setInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch (error) {
      console.error('Failed to cancel invite:', error);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: OrganizationRole) => {
    if (!orgId) return;

    try {
      await updateMemberRole(orgId, userId, newRole);
      setMembers(prev => prev.map(m => m.userId === userId ? { ...m, role: newRole } : m));
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!orgId) return;

    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      await removeMember(orgId, userId);
      setMembers(prev => prev.filter(m => m.userId !== userId));
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to remove member');
    }
  };

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <TopBar />
        <div className="flex items-center justify-center h-[calc(100vh-58px)]">
          <p className="text-gray-500 dark:text-gray-400">Organization not found</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'general' as Tab, label: 'General' },
    { id: 'members' as Tab, label: 'Members' },
    { id: 'billing' as Tab, label: 'Billing' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <TopBar />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Organization Settings
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage {organization.name}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Organization Details
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Organization Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!isOwner}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                  />
                </div>

                {saveMessage && (
                  <p className={`text-sm ${saveMessage.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {saveMessage.text}
                  </p>
                )}

                {isOwner && (
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !name.trim() || name === organization.name}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>
            </div>

            {/* API Token Controls (owner-only) */}
            {isOwner && (
              <ApiTokenControls
                organizationId={organization.id}
                currentValue={organization.allowApiTokens ?? true}
              />
            )}

            {/* Danger Zone */}
            {isOwner && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-900/50 p-6">
                <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
                  Danger Zone
                </h2>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Deleting an organization is permanent and cannot be undone. All projects, tasks, and data will be lost.
                </p>

                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Delete Organization
                </button>
              </div>
            )}
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-6">
            {/* Invite Form (Owner only) */}
            {isOwner && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Invite Member
                </h2>

                <div className="flex space-x-4">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as OrganizationRole)}
                    className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                  >
                    <option value="member">Member</option>
                    <option value="owner">Owner</option>
                  </select>
                  <button
                    onClick={handleInvite}
                    disabled={isInviting || !inviteEmail.trim()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
                  >
                    {isInviting ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>

                {inviteError && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">{inviteError}</p>
                )}
              </div>
            )}

            {/* Members List */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Members ({members.length})
                </h2>
              </div>

              {isLoadingMembers ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {members.map(member => (
                    <div key={member.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-sm font-bold text-white">
                            {(member.user.name || member.user.email || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {member.user.name || member.user.email}
                          </p>
                          {member.user.name && member.user.email && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{member.user.email}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        {isOwner ? (
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateRole(member.userId, e.target.value as OrganizationRole)}
                            className="px-2 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white"
                          >
                            <option value="member">Member</option>
                            <option value="owner">Owner</option>
                          </select>
                        ) : (
                          <span className={`px-2 py-1 text-xs rounded ${
                            member.role === 'owner'
                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                          </span>
                        )}

                        {isOwner && members.filter(m => m.role === 'owner').length > 1 && (
                          <button
                            onClick={() => handleRemoveMember(member.userId)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Invites (Owner only) */}
            {isOwner && invites.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Pending Invites ({invites.length})
                  </h2>
                </div>

                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {invites.map(invite => (
                    <div key={invite.id} className="px-6 py-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{invite.email}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {invite.role} &middot; Expires {new Date(invite.expiresAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCancelInvite(invite.id)}
                        className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Billing
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              Billing information and subscription management coming soon.
            </p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[400] overflow-y-auto">
          <div className="fixed inset-0 bg-black/30 dark:bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteModal(false)} />

          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-2xl">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-500/20">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>

                <h3 className="text-lg font-semibold text-center text-gray-900 dark:text-white mb-2">
                  Delete Organization?
                </h3>

                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                  This action cannot be undone. Type <strong>{organization.name}</strong> to confirm.
                </p>

                <input
                  type="text"
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  placeholder={organization.name}
                  className="w-full px-3 py-2 mb-4 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                />

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeleteConfirmName('');
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleteConfirmName !== organization.name}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors"
                  >
                    Delete Organization
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSettings;

/**
 * Owner-only control to disable PAT issuance + use organization-wide.
 * Disabling immediately invalidates all existing tokens (gateway middleware
 * checks `allowApiTokens` on every PAT-authenticated request).
 */
const ApiTokenControls: React.FC<{
  organizationId: string;
  currentValue: boolean;
}> = ({ organizationId, currentValue }) => {
  const { updateOrganization } = useOrganization();
  const [allowed, setAllowed] = useState(currentValue);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setAllowed(currentValue);
  }, [currentValue]);

  const handleToggle = async () => {
    const next = !allowed;
    // Disabling is destructive (kills existing tokens). Confirm explicitly.
    if (!next) {
      const ok = window.confirm(
        'Disable API tokens? All existing tokens stop working immediately. CLI users will need a new token after you re-enable.',
      );
      if (!ok) return;
    }
    setIsSaving(true);
    setMessage(null);
    try {
      await updateOrganization(organizationId, { allowApiTokens: next });
      setAllowed(next);
      setMessage({ type: 'success', text: next ? 'API tokens enabled.' : 'API tokens disabled.' });
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.error ?? err?.message ?? 'Failed to update setting.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        API Tokens
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Personal Access Tokens are used by the Sente CLI and Claude Code skill. Disable this if
        your organization requires Auth0 SSO + MFA for every authenticated action -- but note that
        members will lose CLI access until you re-enable.
      </p>
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-900 dark:text-white">
          Status: <span className="font-medium">{allowed ? 'Enabled' : 'Disabled'}</span>
        </div>
        <button
          onClick={handleToggle}
          disabled={isSaving}
          className={`px-4 py-2 rounded-lg transition-colors text-white ${
            allowed
              ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
              : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400'
          }`}
        >
          {isSaving ? 'Saving...' : allowed ? 'Disable API tokens' : 'Enable API tokens'}
        </button>
      </div>
      {message && (
        <p
          className={`mt-3 text-sm ${
            message.type === 'success'
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}
        >
          {message.text}
        </p>
      )}
    </div>
  );
};
